import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppWindow, ChevronDown } from "lucide-react";
import { formatDuration } from "../../utils/format";
import DonutChart, { type SliceData } from "./DonutChart";
import TimeChange from "../dashboard/TimeChange";
import { INSIGNIFICANT_COLOR } from "./palette";
import { useExpandableList } from "../../utils/useExpandableList";
import { HelpHint } from "../processes/processSidebarUi";

const VISIBLE_ITEMS = 5;

export default function AllocationDonut({
  data,
  otherLabel,
  additionalSlice,
  expandLabel,
  previousData,
  comparisonLabel,
  comparisonHelp,
}: {
  data: SliceData[];
  /** Omit when the caller already supplies its complete, grouped allocation. */
  otherLabel?: string;
  additionalSlice?: SliceData;
  expandLabel?: string;
  previousData?: Array<{ name: string; value: number }>;
  comparisonLabel?: string;
  comparisonHelp?: string;
}) {
  const { t } = useTranslation();
  const { expanded, changeExpanded, listProps } = useExpandableList();
  const [hoveredName, setHoveredName] = useState<string | null>(null);
  // A sixth item takes the same space as Other, so only group two or more.
  const canExpand = !!otherLabel && data.length > VISIBLE_ITEMS + 1;
  const remaining = data.slice(VISIBLE_ITEMS).reduce((total, item) => total + item.value, 0);
  const slices = expanded || !canExpand ? [...data] : data.slice(0, VISIBLE_ITEMS);
  if (otherLabel && canExpand && !expanded && remaining > 0) {
    slices.push({
      name: otherLabel,
      value: remaining,
      color: INSIGNIFICANT_COLOR,
      onClick: () => {
        changeExpanded(true);
        setHoveredName(null);
      },
    });
  }
  const chartSlices = additionalSlice ? [...slices, additionalSlice] : slices;
  const total = data.reduce((sum, item) => sum + item.value, 0) + (additionalSlice?.value ?? 0);
  const hoveredIndex = chartSlices.findIndex(slice => slice.name === hoveredName);
  const showIcons = data.some(slice => slice.iconUri !== undefined);

  const previousValues = new Map(previousData?.map(row => [row.name, row.value]));
  // Compare Other using the same apps hidden in the current period, not the old top five.
  const previousOther = data
    .slice(VISIBLE_ITEMS)
    .reduce((sum, row) => sum + (previousValues.get(row.name) ?? 0), 0);
  // A column of dashes says nothing, so compare only when some app has a baseline.
  const showChange = data.some(row => (previousValues.get(row.name) ?? 0) > 0);
  const valueColumns = showChange
    ? "grid grid-cols-[minmax(0,1fr)_7.5rem_4.5rem] items-center gap-x-2 @[480px]/legend:gap-x-4"
    : "grid grid-cols-[minmax(0,1fr)_7.5rem] items-center gap-x-3 @[480px]/legend:gap-x-4";
  const renderLabel = (slice: SliceData) => {
    const className = `${valueColumns} min-w-0 flex-1 rounded-lg px-2 py-2 text-left text-sm transition-colors focus-visible:outline-info ${hoveredName === slice.name ? "bg-muted-surface text-text" : "text-text-mid"}`;
    const content = (
      <>
        <span className="flex min-w-0 items-start gap-2.5">
          <span
            className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${slice.hatched ? "opacity-50" : ""}`}
            style={{ backgroundColor: slice.color }}
            aria-hidden="true"
          />
          {showIcons && (
            <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
              {slice.iconUri ? (
                <img src={slice.iconUri} alt="" className="h-5 w-5 object-contain" />
              ) : slice.iconUri === null ? (
                <AppWindow className="h-4 w-4 text-text-dim" />
              ) : null}
            </span>
          )}
          <span className="min-w-0 truncate" title={slice.name}>
            {slice.name}
          </span>
        </span>
        <span className="whitespace-nowrap text-right font-medium tabular-nums text-text">
          {formatDuration(slice.value)}{" "}
          {/* A slot as wide as "(99%)" lines durations up without pushing shares far from them. */}
          <span className="inline-block min-w-11 font-normal text-text-mid">
            (
            {total > 0 && slice.value > 0 && slice.value / total < 0.005
              ? "<1"
              : Math.round(total > 0 ? (slice.value / total) * 100 : 0)}
            %)
          </span>
        </span>
        {showChange && (
          <TimeChange
            current={slice.value}
            previous={
              slice.name === otherLabel ? previousOther : (previousValues.get(slice.name) ?? 0)
            }
            label={comparisonLabel}
          />
        )}
      </>
    );
    const hoverProps = {
      onMouseEnter: () => setHoveredName(slice.name),
      onMouseLeave: () => setHoveredName(null),
      onFocus: () => setHoveredName(slice.name),
      onBlur: () => setHoveredName(null),
    };
    return (
      <li
        key={slice.name}
        className="relative flex min-w-0 items-start gap-1 border-b border-outline/70 py-1 last:border-0"
      >
        {slice.onClick ? (
          <button
            type="button"
            className={className}
            aria-pressed={slice.name === otherLabel ? undefined : false}
            aria-label={slice.name}
            onClick={slice.onClick}
            {...hoverProps}
          >
            {content}
          </button>
        ) : (
          <div
            tabIndex={0}
            className={className}
            aria-label={`${slice.name}: ${formatDuration(slice.value)}${slice.description ? `. ${slice.description}` : ""}`}
            {...hoverProps}
          >
            {content}
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="@container/donut flex flex-1 flex-col">
      <div className="flex flex-col items-center gap-4 @[520px]/donut:flex-row">
        <div className="relative shrink-0">
          <DonutChart
            data={chartSlices}
            width={200}
            height={200}
            outerRadius={90}
            innerRadius={62}
            hoveredIndex={hoveredIndex < 0 ? null : hoveredIndex}
            onHoverChange={index => setHoveredName(index === null ? null : chartSlices[index].name)}
            formatValue={formatDuration}
          />
          {total > 0 && (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-display text-xl font-semibold tabular-nums">
                {formatDuration(total)}
              </span>
              <span className="mt-1 text-xs text-text-mid">{t("dashboard.total_time")}</span>
            </div>
          )}
          {!total && (
            <p className="flex h-[220px] w-[220px] items-center justify-center text-sm text-text-dim">
              {t("dashboard.no_data")}
            </p>
          )}
        </div>
        <div className="@container/legend w-full min-w-0">
          <div
            className={`${valueColumns} overflow-y-hidden [scrollbar-gutter:stable] border-b border-outline px-2 py-3 text-[10px] font-medium uppercase tracking-wider text-text-mid`}
          >
            <span aria-hidden="true">{t("dashboard.app")}</span>
            <span aria-hidden="true" className="text-right">
              {t("dashboard.total_time")}
            </span>
            {showChange && (
              <span className="text-right">
                {comparisonHelp ? (
                  <HelpHint label={t("dashboard.delta_previous")} text={comparisonHelp} standalone>
                    {t("dashboard.delta_previous")}
                  </HelpHint>
                ) : (
                  <span aria-hidden="true">{t("dashboard.delta_previous")}</span>
                )}
              </span>
            )}
          </div>
          {/* Beside the donut, reserve the tallest collapsed list (six 45px rows) so the centred
              donut stays put when a process with fewer apps is selected. */}
          <ul
            {...listProps}
            className="overflow-y-auto [scrollbar-gutter:stable] @[520px]/donut:min-h-[269px]"
          >
            {slices.map(slice => renderLabel(slice))}
          </ul>
        </div>
      </div>
      {canExpand && (
        <button
          type="button"
          className="mt-auto self-start pt-5 inline-flex items-center gap-2 text-sm font-medium text-info hover:underline focus-visible:outline-info"
          aria-expanded={expanded}
          onClick={() => {
            changeExpanded(!expanded);
            setHoveredName(null);
          }}
        >
          {expanded
            ? t("dashboard.show_less_apps")
            : (expandLabel ?? t("dashboard.show_more_apps"))}
          <ChevronDown className={`h-4 w-4 ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
